import React, { useEffect, useRef } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useFocusManagement, useKeyboardNavigation, useScreenReader } from '../../hooks/useAccessibility';
import { buildResponsiveClasses } from '../../utils/responsive';

const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  closeOnOverlayClick = true,
  closeOnEscape = true,
  showCloseButton = true,
  className = '',
  overlayClassName = '',
  contentClassName = '',
  titleId,
  describedBy,
  ...props
}) => {
  const { announce } = useScreenReader();
  const containerRef = useFocusManagement(isOpen);
  const overlayRef = useRef(null);

  // Handle keyboard navigation
  const { onKeyDown } = useKeyboardNavigation({
    onEscape: (event) => {
      if (closeOnEscape) {
        event.preventDefault();
        onClose();
      }
    }
  });

  // Announce modal opening/closing to screen readers
  useEffect(() => {
    if (isOpen) {
      announce(`Modal opened: ${title || 'Dialog'}`);
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scroll
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, title, announce]);

  // Handle overlay click
  const handleOverlayClick = (event) => {
    if (closeOnOverlayClick && event.target === overlayRef.current) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'sm:max-w-sm',
    md: 'sm:max-w-lg',
    lg: 'sm:max-w-2xl',
    xl: 'sm:max-w-4xl',
    full: 'sm:max-w-full sm:m-4'
  };

  const modalId = titleId || 'modal-title';
  const descriptionId = describedBy || 'modal-description';

  return (
    <div
      className={buildResponsiveClasses({
        base: 'fixed inset-0 z-50 overflow-y-auto',
        custom: className
      })}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? modalId : undefined}
      aria-describedby={describedBy ? descriptionId : undefined}
      onKeyDown={onKeyDown}
      {...props}
    >
      {/* Overlay */}
      <div
        ref={overlayRef}
        className={buildResponsiveClasses({
          base: `
            flex items-end justify-center min-h-screen pt-4 px-4 pb-20 
            text-center sm:block sm:p-0
          `,
          custom: overlayClassName
        })}
        onClick={handleOverlayClick}
      >
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          aria-hidden="true"
        />

        {/* Center alignment helper */}
        <span 
          className="hidden sm:inline-block sm:align-middle sm:h-screen" 
          aria-hidden="true"
        >
          &#8203;
        </span>

        {/* Modal content */}
        <div
          ref={containerRef}
          className={buildResponsiveClasses({
            base: `
              inline-block align-bottom bg-card-bg rounded-lg text-left 
              overflow-hidden shadow-xl transform transition-all
              sm:my-8 sm:align-middle sm:w-full
              ${sizeClasses[size]}
            `,
            mobile: 'w-full',
            custom: contentClassName
          })}
        >
          {/* Header */}
          {(title || showCloseButton) && (
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-card-border">
              {title && (
                <h2 
                  id={modalId}
                  className="text-lg font-semibold text-text-primary"
                >
                  {title}
                </h2>
              )}
              
              {showCloseButton && (
                <button
                  type="button"
                  onClick={onClose}
                  className={buildResponsiveClasses({
                    base: `
                      rounded-md text-text-tertiary hover:text-text-secondary 
                      focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                      focus:ring-offset-bg-primary
                      transition-colors duration-200
                    `,
                    mobile: 'p-2 min-h-touch min-w-touch',
                    tablet: 'sm:p-1 sm:min-h-0 sm:min-w-0'
                  })}
                  aria-label="Close modal"
                >
                  <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              )}
            </div>
          )}

          {/* Content */}
          <div className="p-4 sm:p-6">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

Modal.Confirmation = ({
  isOpen,
  onClose,
  onConfirm,
  title = "Confirm Action",
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "danger", // danger, warning, info
  ...props
}) => {
  const variantStyles = {
    danger: {
      icon: '⚠️',
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      confirmButton: 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
    },
    warning: {
      icon: '⚠️',
      iconBg: 'bg-yellow-100',
      iconColor: 'text-yellow-600',
      confirmButton: 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500'
    },
    info: {
      icon: 'ℹ️',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      confirmButton: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
    }
  };

  const style = variantStyles[variant];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      {...props}
    >
      <div className="sm:flex sm:items-start">
        <div className={`
          mx-auto flex-shrink-0 flex items-center justify-center 
          h-12 w-12 rounded-full ${style.iconBg} sm:mx-0 sm:h-10 sm:w-10
        `}>
          <span className={`text-xl ${style.iconColor}`} aria-hidden="true">
            {style.icon}
          </span>
        </div>
        
        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
          <div className="mt-2">
            <p className="text-sm text-text-secondary">
              {message}
            </p>
          </div>
        </div>
      </div>
      
      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-3">
        <button
          type="button"
          onClick={onConfirm}
          className={buildResponsiveClasses({
            base: `
              inline-flex justify-center rounded-md border border-transparent 
              shadow-sm px-4 py-2 font-medium text-white
              focus:outline-none focus:ring-2 focus:ring-offset-2
              transition-colors duration-200
              ${style.confirmButton}
            `,
            mobile: 'w-full text-base min-h-touch',
            tablet: 'sm:ml-3 sm:w-auto sm:text-sm sm:min-h-0'
          })}
        >
          {confirmText}
        </button>
        
        <button
          type="button"
          onClick={onClose}
          className={buildResponsiveClasses({
            base: `
              inline-flex justify-center rounded-md border border-border-secondary 
              shadow-sm px-4 py-2 bg-card-bg font-medium text-text-primary 
              hover:bg-bg-tertiary focus:outline-none focus:ring-2 
              focus:ring-offset-2 focus:ring-blue-500 focus:ring-offset-bg-primary
              transition-colors duration-200
            `,
            mobile: 'mt-3 w-full text-base min-h-touch',
            tablet: 'sm:mt-0 sm:w-auto sm:text-sm sm:min-h-0'
          })}
        >
          {cancelText}
        </button>
      </div>
    </Modal>
  );
};
Modal.Confirmation.displayName = 'Modal.Confirmation';

export default Modal;